import { db } from '../firebase';
import {
  collection, getDocs, getDoc,
  doc, query, serverTimestamp, where
} from 'firebase/firestore';
import { logService } from './logService';
import { addDoc, updateDoc, deleteDoc } from './guardedDb';

const PART_COLLECTION = 'E2B_PARTS'; // 부품 창고 컬렉션 이름
const HISTORY_COLLECTION = 'E2B_PART_HISTORY'; // 🚀 신규: 재고 이력 컬렉션

export const partService = {
  // 1. 전체 부품 목록 불러오기
  getParts: async () => {
    try {
      const q = query(collection(db, PART_COLLECTION));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    } catch (e) {
      console.error("부품 로드 실패:", e);
      return [];
    }
  },

  // 2. 신규 부품 추가
  addPart: async (data) => {
    try {
      const { id: _, ...pureData } = data;
      await addDoc(collection(db, PART_COLLECTION), {
        ...pureData,
        createdAt: serverTimestamp()
      });
      logService.record({
        module: '부품관리', action: 'CREATE',
        target: `부품코드 [${pureData.partNo || pureData.partName || '미지정'}]`,
        details: `신규 부품 등록 (품명: ${pureData.partName || '-'}, 초기재고: ${pureData.stock ?? 0} EA)`
      });
    } catch (e) { throw e; }
  },

  // 3. 부품 정보 업데이트
  updatePart: async (data) => {
    try {
      const { id, ...pureData } = data;
      if (!id) throw new Error("ID가 없습니다.");
      const docRef = doc(db, PART_COLLECTION, id);
      await updateDoc(docRef, {
        ...pureData,
        updatedAt: serverTimestamp()
      });
      logService.record({
        module: '부품관리', action: 'UPDATE',
        target: `부품코드 [${pureData.partNo || pureData.partName || id}]`,
        details: `부품 정보 수정 (품명: ${pureData.partName || '-'}, 재고: ${pureData.stock ?? '-'} EA, 출고가: ${pureData.price ?? '-'})`
      });
    } catch (e) { throw e; }
  },

  // 4. 특정 부품 삭제
  deletePart: async (id) => {
    try {
      if (!id) throw new Error("ID가 없습니다.");
      const docRef = doc(db, PART_COLLECTION, id);
      // 무엇이 지워졌는지 남기기 위해 삭제 전에 품번/품명을 확보한다(감사 목적).
      let label = id;
      let partName = '-';
      try {
        const snap = await getDoc(docRef);
        if (snap.exists()) { label = snap.data().partNo || snap.data().partName || id; partName = snap.data().partName || '-'; }
      } catch (_) { /* 확보 실패해도 삭제는 진행 */ }

      await deleteDoc(docRef);
      logService.record({
        module: '부품관리', action: 'DELETE',
        target: `부품코드 [${label}]`,
        details: `부품 삭제 (품명: ${partName})`
      });
    } catch (e) { throw e; }
  },

  // 5. 품번 중복 체크
  checkDuplicate: async (partNo, excludeId = null) => {
    try {
      const q = query(collection(db, PART_COLLECTION));
      const querySnapshot = await getDocs(q);
      const parts = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      
      return parts.some(p => p.partNo === partNo && p.id !== excludeId);
    } catch (e) {
      console.error("중복 체크 에러:", e);
      return false;
    }
  },

  // 🚀 6. [신규] 재고 변동 이력 저장 (입고, 출고 등)
  addStockHistory: async (partId, historyData) => {
    try {
      await addDoc(collection(db, HISTORY_COLLECTION), {
        partId,
        ...historyData,
        createdAt: serverTimestamp() // 정렬을 위한 타임스탬프
      });
    } catch (e) {
      console.error("이력 저장 에러:", e);
      throw e;
    }
  },

  // 🚀 7. [신규] 특정 부품의 재고 이력 불러오기
  getStockHistory: async (partId) => {
    try {
      // 해당 부품(partId)의 이력만 필터링해서 가져오기
      const q = query(collection(db, HISTORY_COLLECTION), where("partId", "==", partId));
      const querySnapshot = await getDocs(q);
      const histories = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      
      // 프론트엔드에서 최신순(내림차순)으로 정렬하여 반환
      return histories.sort((a, b) => {
        const timeA = a.createdAt ? a.createdAt.seconds : Date.now();
        const timeB = b.createdAt ? b.createdAt.seconds : Date.now();
        return timeB - timeA;
      });
    } catch (e) {
      console.error("이력 로드 에러:", e);
      return [];
    }
  }
};